import {
  Partner, User, UserApi, EventsApi, PartnersApi, UnitsApi, Event, MarketsApi, PartnerWithKeys,
} from '@compassdigital/foodworks.ts.sdk';
import { IEventInfo, IRestaurantInfo } from '@/interface/schedulerTableInfo';
// eslint-disable-next-line
import { filter, orderBy, find } from 'lodash';
import Format from '@/util/Format';
import AppModule from '@/store/modules/app';
// eslint-disable-next-line
import _ from 'lodash';

export default class ScheduleUtil {
  /**
     * Parses the partners and returns formatted array
     * @param partners
     */
  public static getMappedPartners(partners: Array<Partner>) {
    const mappedPartners = !partners
      || partners.map((partner: Partner) => {
        const {
          name,
          id,
        } = partner;
        const partnerInfo: any = {};
        partnerInfo.id = id;
        partnerInfo.partnerName = name;
        return partnerInfo;
      });
    return orderBy(mappedPartners, [user => user.partnerName.toLowerCase()], 'asc');
  }

  /**
     * Parses the popup patters and returns matched patterns
     * @param popupPatterns
     */
  public static schedulePatternCheck(popupPatterns: any) {
    const defaultArray: any = [];
    const mappedArray = popupPatterns.map((value: any) => {
      if (value === 1) {
        defaultArray.push('Monday');
      }
      if (value === 2) {
        defaultArray.push('Tuesday');
      }
      if (value === 3) {
        defaultArray.push('Wednesday');
      }
      if (value === 4) {
        defaultArray.push('Thursday');
      }
      if (value === 5) {
        defaultArray.push('Friday');
      }
      if (value === 6) {
        defaultArray.push('Saturday');
      }
      if (value === 0) {
        defaultArray.push('Sunday');
      }
      return defaultArray;
    });
    return mappedArray;
  }

  public static checkArrays(arrA: any, arrB: any) {
    // check if lengths are different
    if (arrA.length !== arrB.length) return false;
    // slice so we do not effect the orginal
    // sort makes sure they are in order

    const cA = arrA.slice().sort();
    const cB = arrB.slice().sort();
    // eslint-disable-next-line
    for (let i = 0; i < cA.length; i++) {
      if (cA[i] !== cB[i]) return false;
    }
    return true;
  }

  /**
   * returns mapped events
   * @param events
   */
  public static getMappedEvents(events: any) {
    const mappedEvents = !events || events.map((event: any) => {
      const {
        id, date, meta, status, type, unitId, _createdDate, partnerId, _relationship,
      } = event;
      const eventInfo: any = {};
      if (type === 'standard') {
        eventInfo.id = id;
        eventInfo.start = date;
        eventInfo.description = meta.reason;
        eventInfo.status = status;
        eventInfo.type = type;
        eventInfo.unitId = unitId;
        eventInfo.createdDate = _createdDate;
        if (meta.reasonDetails) {
          eventInfo.reasonDetails = meta.reasonDetails;
        }
        if (_relationship) {
          const { partner } = _relationship;
          const { cuisineTypes, name } = partner;
          if (cuisineTypes && name) {
            const cuisineType = cuisineTypes.join();
            eventInfo.cuisineType = cuisineType;
            eventInfo.name = name;
          }
        }
      }
      if (type === 'non-working') {
        eventInfo.id = id;
        eventInfo.start = date;
        eventInfo.name = meta.description;
        eventInfo.status = status;
        eventInfo.type = type;
        eventInfo.unitId = unitId;
        eventInfo.createdDate = _createdDate;
      }
      return eventInfo;
    });
    return (orderBy(mappedEvents, 'createdDate', 'asc'));
  }

  /**
   * Parses the partners users and returns active partners users
   * @param users
   */
  public static activePartners(users: Array<User>) {
    if (!users) return '';
    const activePartners = !users || users.filter((user: any) => {
      const { active, onboarding: { status } } = user;
      if (active === true && status === 'completed') {
        return user;
      }
      return '';
    });
    return orderBy(activePartners, [(user: any) => user.name.toLowerCase()], 'asc');
  }

  /**
   * returns formatted partners
   * @param partners
   */
  public static getActivePartners(partners: any) {
    const mappedPartner = !partners || partners.map((partner: any) => {
      const {
        id, name,
      } = partner;
      const partnerIinfo: any = {};

      partnerIinfo.text = name!;
      partnerIinfo.value = id;
      return partnerIinfo;
    });
    return orderBy(mappedPartner, 'text', 'asc');
  }

  /**
   * Parses the days and event pattern
   * @param patttern
   */
  public static getMappedDays(days: any, pattern: any) {
    if (pattern.length === undefined) return '';
    const popUp = !pattern || pattern.map((day: any) => {
      switch (day) {
        case 0:
          return 'Sunday';
        case 1:
          return 'Monday';
        case 2:
          return 'Tuesday';
        case 3:
          return 'Wednesday';
        case 4:
          return 'Thursday';
        case 5:
          return 'Friday';
        case 6:
          return 'Saturday';
        default:
          return '';
      }
    });
    const fetchPattern = popUp.filter((day: any) => day === days);
    return fetchPattern;
  }

  /**
   * Parses the events and date
   * @param events
   */
  public static getUnavailableEvents(
    events: Array<Event>, date: Date, partners: Array<Partner>,
  ) {
    if (!events) return '';
    const selectedEvents = !events || events.filter((event: any) => {
      const { status, type } = event;
      if (event.date === date
        && status !== 'canceled'
        && status !== 'declined'
        && type !== 'non-working'
      ) {
        return event;
      }
      return '';
    });
    const eventFormatted = !selectedEvents || selectedEvents.map((event: Event) => {
      const { meta } = event;
      const eventInfo: any = {};
      if (meta !== undefined) {
        const { reason } = meta;
        eventInfo.name = reason;
      }
      eventInfo.status = event.status;
      eventInfo.type = event.type;
      eventInfo.id = event.id;
      return eventInfo;
    });
    return this.getMappedEventsName(eventFormatted, partners, date);
  }

  /**
   * Parses the events and event meta
   * @param events
   */
  public static getMappedEventsName(
    events: Array<IEventInfo>,
    partners: Array<Partner>,
    selectedDate: Date,
  ) {
    if (events) {
      const eventFirst = partners.filter((o1) => events.some((o2) => o1.name === o2.name));
      if (eventFirst.length > 0) {
        const eventSchedule = !eventFirst || eventFirst.map((eventPartner: any) => {
          const { name, schedule } = eventPartner;
          const { servicableLocations } = schedule;
          if (servicableLocations === 1) {
            return this.commonUnavailableEvents(events, partners);
          }
          if (servicableLocations === 2) {
            const restaurant = !events || events.filter((e) => e.name === name);
            if (restaurant.length <= 1) {
              const eventObj: any = {};
              eventObj.availableEvent = eventFirst;
              eventObj.unAvailableEvent = '';
              return eventObj;
            }
            return this.commonUnavailableEvents(events, partners);
          }
          if (servicableLocations === 3) {
            const restaurant = !events || events.filter((e) => e.name === name);
            if (restaurant.length <= 2) {
              const eventObj: any = {};
              eventObj.availableEvent = eventFirst;
              eventObj.unAvailableEvent = '';
              return eventObj;
            }
            return this.commonUnavailableEvents(events, partners);
          }
          if (servicableLocations === 4) {
            const restaurant = !events || events.filter((e) => e.name === name);
            if (restaurant.length <= 3) {
              const eventObj: any = {};
              eventObj.availableEvent = eventFirst;
              eventObj.unAvailableEvent = '';
              return eventObj;
            }
            return this.commonUnavailableEvents(events, partners);
          }
          if (servicableLocations === 5) {
            const restaurant = !events || events.filter((e) => e.name === name);
            if (restaurant.length <= 4) {
              const eventObj: any = {};
              eventObj.availableEvent = eventFirst;
              eventObj.unAvailableEvent = '';
              return eventObj;
            }
            return this.commonUnavailableEvents(events, partners);
          }
          return this.commonUnavailableEvents(events, partners);
        });
        return eventSchedule[0];
      }
      return this.commonUnavailableEvents(events, partners);
    }
    return this.commonUnavailableEvents(events, partners);
  }

  public static commonUnavailableEvents(events: any, partners: any) {
    if (!events) return '';
    const eventFirst = partners.filter((o1: any) => events.some((o2: any) => o1.name === o2.name));
    const availableEvents = _(partners)
      .differenceBy(eventFirst, 'name')
      .map(_.partial(_.pick, _, 'name'))
      .value();
    const eventObj: any = {};
    eventObj.availableEvent = availableEvents;
    eventObj.unAvailableEvent = eventFirst;
    return eventObj;
  }

  public static sortArrayCalendarFormat(days: any) {
    const calendarDays = days;
    const sorter = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };

    // eslint-disable-next-line
    calendarDays.sort(function sortByDay(a: any, b: any) {
      const day1 = a.toLowerCase();
      const day2 = b.toLowerCase();
      return sorter[day1] - sorter[day2];
    });
    return calendarDays;
  }

  public static findAvailableRest(partners: any, selectedDate: any) {
    const userDay = Format.getFormatDay(selectedDate);
    const restaurantPattern = !partners || partners.map((partner: any) => {
      const {
        schedule: { popupPattern, servicableLocations },
        name,
        id,
        cuisineTypes,
      } = partner;
      let cuisine;
      if (cuisineTypes) {
        cuisine = cuisineTypes.join();
      }
      const partnreInfo: any = {};
      partnreInfo.name = name;
      partnreInfo.id = id;
      partnreInfo.cuisneType = cuisine;
      partnreInfo.servicableLocations = servicableLocations;
      if (popupPattern !== undefined) {
        const pattern = this.getRestaurantPattern(userDay, popupPattern);
        if (pattern.length === 0) {
          partnreInfo.availableRest = false;
        }
        if (pattern.length > 0) {
          partnreInfo.availableRest = true;
        }
      }
      return partnreInfo;
    });
    return restaurantPattern;
  }

  public static async getNonServiceableEvents(
    partners: Array<IRestaurantInfo>, eventDate: string, marketEvents: Array<Event>,
  ) {
    const maxServiceablePartners = !partners || partners.map((partner: IRestaurantInfo) => {
      const { id, servicableLocations } = partner;
      const parsedDate = new Date(eventDate).getDate();
      const parsedMonth = new Date(eventDate).getMonth() + 1;
      const events = marketEvents
        .filter((e: Event) => {
          const { partnerId, date } = e;
          if (date) {
            const eDate = new Date(date).getDate();
            const eventMonth = new Date(date).getMonth() + 1;
            if (partnerId === id
              && (eDate === parsedDate && eventMonth === parsedMonth)) {
              return e;
            }
          }
          return '';
        }).filter((e: any) => e !== '');
      if (servicableLocations) {
        if (events.length >= servicableLocations) {
          // eslint-disable-next-line no-param-reassign
          partner.isMaxedOut = true;

          // eslint-disable-next-line no-param-reassign
          partner.maxedOutReason = `This restaurant has reached <br /> max serviceable
          locations (${servicableLocations})`;
        } else {
          // eslint-disable-next-line no-param-reassign
          partner.isMaxedOut = false;
        }
        return partner;
      }
      return '';
    });
    return maxServiceablePartners;
  }

  public static getUnavailableEventsFiltered(partners: Array<PartnerWithKeys>) {
    return partners.map((partner: any) => {
      if (partner.isMaxedOut) {
        // eslint-disable-next-line no-param-reassign
        partner.maxedOutReason = `This restaurant has reached <br /> max serviceable
        locations (${partner.schedule?.servicableLocations})`;
      }
      return partner;
    });
  }

  /**
   * Parses the restaurants pattenrs and days
   * @param patttern
   * @param days
   */
  public static getRestaurantPattern(days: any, pattern: any) {
    const popUp = !pattern || pattern.map((day: any) => {
      switch (day) {
        case 0:
          return 'Sunday';
        case 1:
          return 'Monday';
        case 2:
          return 'Tuesday';
        case 3:
          return 'Wednesday';
        case 4:
          return 'Thursday';
        case 5:
          return 'Friday';
        case 6:
          return 'Saturday';
        default:
          return '';
      }
    });
    const fetchPattern = popUp.filter((day: any) => day === days);
    return fetchPattern;
  }

  /**
   * Parses the rest and fetchedEvents
   * @param rest
   * @param fetchedEvents
   */
  public static getFilteredData(rest: any, fetchedEvents: any, linkedEvents: any) {
    const eventOne = !rest || rest.filter((item: any) => item.availableRest === false);
    const eventSecond = !rest || rest.filter((item: any) => item.availableRest === true);
    const newObj: any = {};
    if (linkedEvents.length === 0) {
      newObj.unavailableRest = eventOne;
      newObj.availableRest = eventSecond;
    }
    if (linkedEvents.length > 0) {
      // eslint-disable-next-line
      // const newEvent = eventOne.filter((o1:any) => linkedEvents.some((o2: any) => o1.id === o2.id));
      // eslint-disable-next-line
      const upEvent = eventSecond.filter((o1: any) => linkedEvents.some((o2: any) => o1.id === o2.id));
      const finaldisableEvents = upEvent.concat(eventOne);
      const availableEvents = rest.filter((e: any) => !finaldisableEvents.includes(e));
      // const availableEvents = _(rest)
      //   .differenceBy(finaldisableEvents, 'id')
      //   .map(_.partial(_.pick, _, 'id'))
      //   .value();
      newObj.unavailableRest = finaldisableEvents;
      newObj.availableRest = availableEvents;
    }
    return newObj;
  }

  /**
   * returns value of disable
   * @param start
   */
  public static disablePublishPartner(start: any) {
    const currentDate = new Date();
    const currentMonthDate = Format.getSchedulerDate(currentDate);
    if (!start) {
      return false;
    }
    const { month, year, date } = start;
    const changedDate = Format.getSchedulerDate(date);
    if (currentMonthDate > changedDate) {
      return true;
    }
    return false;
  }

  /**
   * returns cuisine
   * @param partnerID
   */
  public static async getCuisinePartner(partnerID: string) {
    try {
      const api = new PartnersApi(AppModule.apiConfig);
      const cuisineData: any = await api.getPartnerById(partnerID);
      const { cuisineTypes } = cuisineData.data;
      let cuisine: string = '';
      if (cuisineTypes) {
        cuisine = cuisineTypes.join();
      }
      return cuisine;
    } catch (err) {
      throw err;
    }
  }

  /**
   * returns filtered events
   * @param events
   * @param publishedDate
   */
  public static getPublishedEvents(events: any, publishedDate: any) {
    const currentMonthDate = Format.getSchedulerDate(publishedDate);
    const mappedEvents = !events || events.map((event: any) => {
      const { date, status } = event;
      const availableDatePublish = Format.getSchedulerDate(date);
      const eventInfo: any = {};
      eventInfo.availablePublishDates = availableDatePublish;
      eventInfo.status = status;
      return eventInfo;
    });

    const publishUsers = !mappedEvents || mappedEvents.filter((date: any) => {
      if (date.availablePublishDates === currentMonthDate && date.status === 'draft') {
        return date;
      }
      return '';
    });
    return publishUsers;
  }

  /**
   * returns filtered events
   * @param events
   * @param publishedDate
   */
  public static getPublishedUnitsEvent(events: any, publishedDate: any) {
    const currentMonthDate = Format.getSchedulerDate(publishedDate);
    const mappedEvents = !events || events.map((event: any) => {
      const { date, status } = event;
      const availableDatePublish = Format.getSchedulerDate(date);
      const eventInfo: any = {};
      eventInfo.availablePublishDates = availableDatePublish;
      eventInfo.status = status;
      return eventInfo;
    });

    const publishUsers = !mappedEvents || mappedEvents.filter((date: any) => {
      if (date.availablePublishDates === currentMonthDate && date.status === 'accepted') {
        return date;
      }
      return '';
    });
    return publishUsers;
  }


  /**
   * returns cancel event flag
   * @param events
   * @param date
   */
  public static getPartnerFlag(events: any, selectedDate: any) {
    const mappedEvents = !events || events.filter((event: any) => {
      const {
        id, date, status,
      } = event;

      let checkCancelEvent: boolean = false;
      if (date === selectedDate && status === 'canceled') {
        checkCancelEvent = true;
      } else {
        checkCancelEvent = false;
      }
      return checkCancelEvent;
    });
    return mappedEvents;
  }

  /**
   * returns mapped events
   * @param events
   */
  public static getPartnerRoleEvents(events: any) {
    const mappedEvents = !events || events.map((event: any) => {
      const {
        id, date, meta, status, type, unitId, _createdDate, _relationship,
      } = event;
      const eventInfo: any = {};
      eventInfo.id = id;
      eventInfo.start = date;
      // eventInfo.name = meta.description;
      eventInfo.status = status;
      eventInfo.type = type;
      eventInfo.unitId = unitId;
      eventInfo.createdDate = _createdDate;
      if (_relationship) {
        const { unit: { name } } = _relationship;
        eventInfo.unitName = name;
      }
      if (meta.reasonDetails) {
        eventInfo.reasonDetails = meta.reasonDetails;
      }
      return eventInfo;
    });
    return (orderBy(mappedEvents, 'createdDate', 'asc'));
  }

  /**
   * returns filtered events
   * @param events
   * @param publishedDate
   */
  public static getCancelEvent(events: any, publishedDate: any) {
    const mappedEvents = !events || events.map((event: any) => {
      const {
        start, status, name, id,
      } = event;
      const eventInfo: any = {};
      if (name && start && status) {
        eventInfo.name = name;
        eventInfo.availablePublishDates = start;
        eventInfo.status = status;
        eventInfo.id = id;
        return eventInfo;
      }
      return '';
    });
    const publishUsers = !mappedEvents || mappedEvents.filter((event: any) => {
      if (event.availablePublishDates === publishedDate && event.status === 'canceled') {
        return event;
      }
      return '';
    });
    return publishUsers;
  }

  /**
   * returns filtered events
   * @param events
   * @param publishedDate
   */
  public static getNonResponseEvents(events: any, publishedDate: any) {
    const mappedEvents = !events || events.map((event: any) => {
      const {
        start, status, name, id,
      } = event;
      const eventInfo: any = {};
      if (name && start && status) {
        eventInfo.name = name;
        eventInfo.availablePublishDates = start;
        eventInfo.status = status;
        eventInfo.id = id;
        return eventInfo;
      }
      return '';
    });
    const publishUsers = !mappedEvents || mappedEvents.filter((event: any) => {
      if (event.availablePublishDates === publishedDate && event.status === 'no-response') {
        return event;
      }
      return '';
    });
    return publishUsers;
  }

  /**
   * returns filtered events
   * @param availableEvents
   * @param cancelEvents
   */
  public static getFinalEvents(availableEvent: any, cancelEvent: any) {
    const userEvent: any = {};
    // eslint-disable-next-line
    // const canceledEvents = availableEvent.filter((o1:any) => cancelEvent.some((o2: any) => o1.name === o2.name));
    // eslint-disable-next-line
    const matchEvent = availableEvent.filter((o1: any) => cancelEvent.some((o2: any) => o1.name !== o2.name));
    userEvent.cancelEvent = cancelEvent;
    userEvent.availableEvent = matchEvent;
    return userEvent;
  }

  /**
   * returns filtered events
   * @param availableEvents
   * @param cancelEvents
   */
  public static getEventsAfterNoResponse(availableEvent: any, noResponseEvents: any) {
    const userEvent: any = {};
    // eslint-disable-next-line
    // const canceledEvents = availableEvent.filter((o1:any) => noResponseEvents.some((o2: any) => o1.name === o2.name));
    // eslint-disable-next-line
    const matchEvent = availableEvent.filter((o1: any) => noResponseEvents.some((o2: any) => o1.name !== o2.name));
    userEvent.noResponseEvents = noResponseEvents;
    userEvent.availableEvent = matchEvent;
    return userEvent;
  }

  /**
   * returns mapped popup pattern value
   * @param pattern
   * @param days
   */
  public static getUnitPopupPattern(popupPattern: any) {
    const calendarDays: any = {};
    let mappedPattern;
    const sortPattern = popupPattern.sort((a: any, b: any) => a - b);
    const defaultDayPattern = [0, 1, 2, 3, 4, 5, 6];
    if (sortPattern) {
      mappedPattern = defaultDayPattern.filter((day: any) => !sortPattern.includes(day));
    }
    if (mappedPattern) {
      const mappedData = !mappedPattern || mappedPattern.map((day: any) => {
        if (day === 0) {
          calendarDays.sunday = true;
        }
        if (day === 1) {
          calendarDays.monday = true;
        }
        if (day === 2) {
          calendarDays.tuesday = true;
        }
        if (day === 3) {
          calendarDays.wednesday = true;
        }
        if (day === 4) {
          calendarDays.thursday = true;
        }
        if (day === 5) {
          calendarDays.friday = true;
        }
        if (day === 6) {
          calendarDays.saturday = true;
        }
        return calendarDays;
      });
      return mappedData;
    }
    return '';
  }

  /**
   * returns mapped restaurant with filtered events
   * @param restaurant
   * @param publishDate
   */
  public static async getAsteriskEvents(restaurant: any, publishDate: any) {
    const currentMonthDate = Format.getSchedulerDate(publishDate);
    const mappedEvents = !restaurant || restaurant.map(async (event: any) => {
      const {
        text, value,
      } = event;
      const eventInfo: any = {};
      eventInfo.value = value;
      eventInfo.text = text;
      if (value) {
        const draftEvents = await this.getDarftEvents(value, currentMonthDate);
        eventInfo.draftEvents = draftEvents;
      }
      return eventInfo;
    });
    return Promise.all(orderBy(mappedEvents, 'createdDate', 'asc'));
  }

  /**
   * returns draft events
   * @param partnerID
   * @param publishDate
   */
  public static async getDarftEvents(partnerID: string, publishDate: any) {
    try {
      const startDate = '2020-08-01';
      const endDate: string = '2027-08-01';
      const nullDate: any = undefined;
      const api = new PartnersApi(AppModule.apiConfig);
      const partnerEvent = await api.getPartnerEvents(
        partnerID, nullDate, startDate, endDate,
      );
      const eventData: any = partnerEvent.data;
      if (eventData) {
        const eventUpdateUser = !eventData || eventData.filter((event: any) => {
          const { date, status } = event;
          const formattedDate = Format.getSchedulerDate(date);
          if (formattedDate === publishDate && status === 'draft') {
            return event;
          }
          return null;
        });
        return eventUpdateUser;
      }
      return '';
    } catch (err) {
      throw err;
    }
  }

  /**
   * returns mapped units with filtered events
   * @param units
   * @param publishDate
   */
  public static async getAestrickUnit(units: any, publishDate: any) {
    const currentMonthDate = Format.getSchedulerDate(publishDate);
    const mappedEvents = !units || units.map(async (event: any) => {
      const {
        id, unitName,
      } = event;
      const eventInfo: any = {};
      eventInfo.id = id;
      eventInfo.unitName = unitName;
      if (id) {
        const acceptedEvents = await this.getAcceptedEvent(id, currentMonthDate);
        eventInfo.acceptedEvent = acceptedEvents;
      }
      return eventInfo;
    });
    return Promise.all(orderBy(mappedEvents, 'createdDate', 'asc'));
  }

  /**
   * returns accepted events
   * @param unitId
   * @param publishDate
   */
  public static async getAcceptedEvent(unitID: string, publishDate: any) {
    try {
      const startDate = '2020-08-01';
      const endDate: string = '2027-08-01';
      const nullDate: any = undefined;
      const api = new UnitsApi(AppModule.apiConfig);
      const events: any = await api.getUnitEvents(
        unitID, nullDate, startDate, endDate,
      );
      const eventData: any = events.data;
      if (eventData) {
        const eventUpdateUser = !eventData || eventData.filter((event: any) => {
          const { date, status } = event;
          const formattedDate = Format.getSchedulerDate(date);
          if (formattedDate === publishDate && status === 'accepted') {
            return event;
          }
          return null;
        });
        return eventUpdateUser;
      }
      return '';
    } catch (err) {
      throw err;
    }
  }

  /**
   * returns filtered events
   * @param events
   */
  public static getSelectedDates(events: any) {
    const mappedEvents = !events || events.filter((event: any) => {
      const {
        id, date, status,
      } = event;

      if (status === 'draft'
        || status === 'accepted'
        || status === 'pending'
        || status === 'complete'
        || status === 'no-show'
        || status === 'active'
        || status === 'live'
      ) {
        return event;
      }
      return '';
    });

    const filteredDate = !mappedEvents || mappedEvents.map((event: any) => {
      const { start } = event;
      return start;
    });
    return filteredDate;
  }

  /**
   * returns search events filtered
   * @param events
   * @param search
   */
  public static getFilteredEventSearch(events: any, search: string) {
    if (events.length > 0) {
      let filteredItems = events;
      if (search) {
        filteredItems = filteredItems.filter((item: any) => ((item.name).toLowerCase())
          .includes(search.toLowerCase()));
      }
      return filteredItems;
    }
    return '';
  }
}
